/**
 * The ONE »was wartet noch auf dich?« arithmetic (§1, S1).
 *
 * Two places show the person how much still awaits them at the Lebenslauf: the shell's
 * sidebar badge on `/compass/cv` and the CV-Werkbank's hero chip. They used to derive that
 * number independently — the shell summed THREE terms, the Werkbank FOUR — so the same
 * screen showed 24 and 25 and read as two different entities. This module is the single
 * arithmetic both consume, so the badge, the chip and the body copy can never diverge again.
 *
 * Shape of the seam:
 *   - {@link deriveResolutionCounts} is PURE — it takes the four already-held collections and
 *     returns the counts. The Werkbank holds them as SIGNALS, the shell fetches them; both
 *     hand plain data in, so there is exactly one place the total is computed.
 *   - {@link promotableFrom} is the shared »which uploaded document is still un-promoted?«
 *     rule, lifted verbatim out of the Werkbank's `loadPromotable()`.
 *   - {@link fetchResolutionCounts} is a thin wrapper for the caller (the shell) that holds no
 *     stores: it reads the EXISTING BFF routes and hands the result to `deriveResolutionCounts`.
 *
 * NO new endpoint, NO new event: everything here rides reads that already exist.
 */

import { apiGet } from "../services"

/** An evidence claim, reduced to what the count needs (`EvidenceClaimView` and the raw
 *  `/api/evidence` row both satisfy it). */
export interface ClaimRef {
  status?: string
}

/** A typed asset, reduced to what the count + the promotable rule need (`AssetView` and the
 *  raw `/api/assets` row both satisfy it). */
export interface AssetRef {
  status?: string
  sourceDocumentId?: string
}

/** An uploaded ingest document, reduced to what the promotable rule needs. */
export interface IngestDocRef {
  ingestId: string
  documentId: string
  kind: string
}

/** The four collections the number is made of, exactly as its two consumers already hold them. */
export interface ResolutionInput {
  /** The open CV proposal's entries (`/api/cv/proposal` `.entries`, or the Werkbank's `proposal` signal). */
  cvProposals?: readonly unknown[]
  /** Every known evidence claim — `proposed` ones are the open decisions. */
  claims?: readonly ClaimRef[]
  /** Every known asset — `proposed` ones still await a confirm. */
  assets?: readonly AssetRef[]
  /** Uploaded documents that can still be promoted (see {@link promotableFrom}). */
  promotable?: readonly unknown[]
}

/** The per-term breakdown plus the ONE total every surface shows. */
export interface ResolutionCounts {
  /** AI-proposed CV entries awaiting accept/discard. */
  cvProposals: number
  /** Evidence claims still `proposed`. */
  openClaims: number
  /** Nachweise still `proposed`. */
  proposedAssets: number
  /** Uploaded documents not yet promoted to a typed Nachweis. */
  promotableDocs: number
  /** The sum — the ONLY number a badge, chip or sentence may show for "awaits you". */
  total: number
}

/** Length of a value that SHOULD be an array — tolerant, because these come off the wire. */
function len(v: unknown): number {
  return Array.isArray(v) ? v.length : 0
}

/** Count of array members matching `pred` — tolerant of a non-array (a wire shape can drift). */
function countWhere<T>(v: readonly T[] | undefined, pred: (item: T) => boolean): number {
  return Array.isArray(v) ? v.filter(pred).length : 0
}

/**
 * THE arithmetic. Pure and total: a missing/odd collection counts as zero, never a throw.
 * Four terms — proposed CV entries + open claims + proposed Nachweise + promotable documents
 * — because all four are decisions the person, and only the person, can close.
 */
export function deriveResolutionCounts(input: ResolutionInput): ResolutionCounts {
  const cvProposals = len(input.cvProposals)
  const openClaims = countWhere(input.claims, (c) => c?.status === "proposed")
  const proposedAssets = countWhere(input.assets, (a) => a?.status === "proposed")
  const promotableDocs = len(input.promotable)
  return {
    cvProposals,
    openClaims,
    proposedAssets,
    promotableDocs,
    total: cvProposals + openClaims + proposedAssets + promotableDocs,
  }
}

/**
 * The shared promotable rule: an uploaded ingest document is still promotable while NO asset
 * names it as its source document. Lifted verbatim from the Werkbank's `loadPromotable()` so
 * both consumers apply the same rule — asset status is deliberately IGNORED here (a proposed
 * asset already claims its document; it is counted by `proposedAssets`, not twice).
 */
export function promotableFrom<D extends { documentId: string }>(
  ingestDocs: readonly D[] | undefined,
  assets: readonly AssetRef[] | undefined,
): D[] {
  if (!Array.isArray(ingestDocs)) return []
  const alreadyPromoted = new Set(
    (Array.isArray(assets) ? assets : []).map((a) => a?.sourceDocumentId).filter((id): id is string => Boolean(id)),
  )
  return ingestDocs.filter((d) => !alreadyPromoted.has(d.documentId))
}

/** The JSON reader this module needs — `apiGet` in the app, a stub in tests. */
export type JsonGet = <T>(path: string) => Promise<T>

/** Every uploaded document across every ingest run (the promotable CANDIDATES, before the
 *  already-promoted ones are subtracted). A failed read yields none — never a partial lie. */
async function fetchIngestDocs(get: JsonGet, runs: unknown): Promise<IngestDocRef[]> {
  if (!Array.isArray(runs)) return []
  try {
    const perRun = await Promise.all(
      (runs as Array<{ id?: string }>).map(async (run) => {
        const ingestId = String(run?.id ?? "").replace(/^urn:career:ingest:/, "")
        if (!ingestId) return []
        const detail = await get<{ documents?: Array<{ id: string; kind: string }> }>(`/api/ingest/${ingestId}`)
        return (detail?.documents ?? []).map((d) => ({ ingestId, documentId: d.id, kind: d.kind }))
      }),
    )
    return perRun.flat()
  } catch {
    return []
  }
}

/**
 * Read the four EXISTING BFF routes and resolve them through {@link deriveResolutionCounts}.
 * For the caller that holds no stores (the shell). Rejects on a failed read — the caller
 * decides what "unknown" means (the shell shows no badge rather than a fabricated count).
 */
export async function fetchResolutionCounts(get: JsonGet = apiGet): Promise<ResolutionCounts> {
  const [proposal, claims, assets, runs] = await Promise.all([
    get<{ entries?: unknown[] }>("/api/cv/proposal"),
    get<ClaimRef[]>("/api/evidence"),
    get<AssetRef[]>("/api/assets"),
    get<Array<{ id?: string }>>("/api/ingest"),
  ])
  const ingestDocs = await fetchIngestDocs(get, runs)
  return deriveResolutionCounts({
    cvProposals: proposal?.entries,
    claims,
    assets,
    promotable: promotableFrom(ingestDocs, assets),
  })
}
